import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { client, authorizeClient } from '../../networking/client';
import { toast } from 'react-toastify';
import { RootState } from '../store';
import { generateQueryParams } from '../../helpers';

interface SickLeaveInitialState {
  status: string;
  sickLeave: string;
  prescription: string;
  sickLeaveDownloadLinks: Array<any>;
  prescriptionDownloadLinks: Array<any>;
  anamnesis: Array<any>;
  anamnesisHistory: Array<any>;
  anamnesisState: Array<any>;
}

const initialState: SickLeaveInitialState = {
  status: 'idle',
  sickLeave: '',
  prescription: '',
  sickLeaveDownloadLinks: [],
  prescriptionDownloadLinks: [],
  anamnesis: [],
  anamnesisHistory: [],
  anamnesisState: [],
};

export const postSickLeave = createAsyncThunk(
  'post-healthpoint/sick-leave',
  async (data: any, thunkAPI) => {
    try {
      const res: any = await client.post('/healthpoint/sick-leave', data);
      return res;
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? 'Something went wrong.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return thunkAPI.rejectWithValue(err?.response?.data?.error ?? {});
    }
  }
);

export const getSickLeave = createAsyncThunk(
  'get-healthpoint/sick-leave',
  async (workOrderId: string, thunkAPI) => {
    try {
      const res: any = await client.get(`/healthpoint/sick-leave/${workOrderId}`);
      return res;
    } catch (err: any) {
      // toast.error(err?.response?.data?.error ?? 'Something went wrong.', {
      //     position: toast.POSITION.BOTTOM_RIGHT,
      // });
      // return thunkAPI.rejectWithValue(err?.response?.data?.error ?? {});
    }
  }
);

export const getSickLeaveDownloadLinks = createAsyncThunk(
  'get-healthpoint/sick-leave-download-links',
  async (accountNumber: string, { dispatch, rejectWithValue }) => {
    try {
      const res: any = await authorizeClient.get(`/core/merchant/${accountNumber}/sick_leave`);
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const postPrescription = createAsyncThunk(
  'post-healthpoint/prescription',
  async (data: any, thunkAPI) => {
    try {
      const res: any = await client.post('/healthpoint/prescription', data);
      return res;
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? 'Something went wrong.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return thunkAPI.rejectWithValue(err?.response?.data?.error ?? {});
    }
  }
);

export const getPrescription = createAsyncThunk(
  'get-healthpoint/prescription',
  async (workOrderId: string, { dispatch, rejectWithValue }) => {
    try {
      const res: any = await client.get(`/healthpoint/prescription/${workOrderId}`);
      return res;
    } catch (err: any) {
      // return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

interface AnamnesisParams {
  personId: string;
  language?: string;
  show_history?: boolean;
}

export const getAnamnesis = createAsyncThunk(
  'get-healthpoint/anamnesis',
  async (
    { personId, language, show_history }: AnamnesisParams,
    { dispatch, rejectWithValue }
  ) => {
    try {
      let queryParams = generateQueryParams({
        language: language,
        show_history: show_history,
      });
      const res: any = await client.get(`/core/merchant/${personId}/anamnesis${queryParams}`);
      return { data: res.data, showHistory: show_history };
    } catch (err: any) {
      // return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const postAnamnesis = createAsyncThunk(
  'post-healthpoint/anamnesis',
  async (data: any, thunkAPI) => {
    try {
      const res: any = await client.post('/healthpoint/anamnesis', data);
      return res;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data?.error ?? {});
    }
  }
);

export const getPrescriptionDownloadLinks = createAsyncThunk(
  'get-healthpoint/prescription-download-links',
  async (accountNumber: string, { dispatch, rejectWithValue }) => {
    try {
      const res: any = await authorizeClient.get(`/core/merchant/${accountNumber}/prescription`);
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const sickLeaveAndPrescriptionSlice = createSlice({
  name: 'sickLeave',
  initialState,
  reducers: {
    setAnamnasisState: (state, action) => {
      const updatedState = state.anamnesisState.map((el) => {
        if (el.question_id === action.payload.question_id) {
          return { ...el, answer_comment: action.payload.value, answer: action.payload.checkbox };
        }
        return { ...el };
      });
      state.anamnesisState = [...updatedState];
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(postSickLeave.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(postSickLeave.fulfilled, (state, action) => {
        toast.success('Sick Leave added successfully.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'fulfilled';
      })
      .addCase(postSickLeave.rejected, (state, action: any) => {
        state.status = 'rejected';
      })
      .addCase(getSickLeave.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(getSickLeave.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.sickLeave = action.payload?.data?.prescription?.comment;
      })
      .addCase(getSickLeave.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(getSickLeaveDownloadLinks.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(getSickLeaveDownloadLinks.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.sickLeaveDownloadLinks = action.payload?.data;
      })
      .addCase(getSickLeaveDownloadLinks.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(getPrescriptionDownloadLinks.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(getPrescriptionDownloadLinks.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.prescriptionDownloadLinks = action.payload?.data;
      })
      .addCase(getPrescriptionDownloadLinks.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(postPrescription.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(postPrescription.fulfilled, (state) => {
        toast.success('Prescription was added successfully.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'fulfilled';
      })
      .addCase(postPrescription.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(getPrescription.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(getPrescription.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.prescription = action.payload?.data?.prescription?.comment;
      })
      .addCase(getPrescription.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(getAnamnesis.pending, (state) => {
        state.status = 'pending';
      })

      .addCase(getAnamnesis.fulfilled, (state, action) => {
        state.status = 'fulfilled';

        if (action.payload) {
          const { data, showHistory } = action.payload;

          if (showHistory) {
            state.anamnesisHistory = data;
          } else {
            const anamnesisState = data
              .map((el: any) => el.anamnesis_questions.map((e: any) => ({ ...e })))
              .flat();

            state.anamnesis = data;
            state.anamnesisState = anamnesisState ?? [];
          }
        }
      })
      .addCase(getAnamnesis.rejected, (state, action: any) => {
        state.status = 'rejected';
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })

      .addCase(postAnamnesis.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(postAnamnesis.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        toast.success('Anamnesis was added successfully.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .addCase(postAnamnesis.rejected, (state, action: any) => {
        state.status = 'rejected';
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      });
  },
});

export const { setAnamnasisState, resetState } = sickLeaveAndPrescriptionSlice.actions;

export const slectSickLeaveStatus = (state: RootState) => state.sickLeaveAndPrescription.status;
export const selectAnamnesis = (state: RootState) => state.sickLeaveAndPrescription.anamnesis;
export const selectAnamnesisState = (state: RootState) =>
  state.sickLeaveAndPrescription.anamnesisState;
export const selectStatus = (state: RootState) =>
  state.sickLeaveAndPrescription.status;
export const selectSickLeaveAndPrescriptionStatus = (state: RootState) =>
  state.sickLeaveAndPrescription.status;
export const selectAnamnesisHistory = (state: RootState) =>
  state.sickLeaveAndPrescription.anamnesisHistory;
export default sickLeaveAndPrescriptionSlice.reducer;